import { action, computed, makeObservable, observable, runInAction } from 'mobx'
import { DateTime } from 'luxon'
import type { FirebaseRepository } from '../models/FirebaseRepository'
import { countRoomStatesInRange } from '../firestore/RoomState'
import { Cubit } from './core'

export interface WeeklyDataPoint {
  date: DateTime
  label: string
  count: number
}

export interface DailyDataPoint {
  time: DateTime
  label: string
  count: number
}

export class RoomCountCubit extends Cubit {
  repository: FirebaseRepository

  @observable selectedWeekStart: DateTime
  @observable selectedDay: DateTime
  @observable weeklyData: WeeklyDataPoint[] = []
  @observable dailyData: DailyDataPoint[] = []
  @observable loadingWeekly = false
  @observable loadingDaily = false

  constructor(repository: FirebaseRepository) {
    super()
    makeObservable(this)
    this.repository = repository
    this.selectedWeekStart = DateTime.now().startOf('week')
    this.selectedDay = DateTime.now().startOf('day')
  }

  initialize(): void {
    this.fetchWeeklyData()
    this.fetchDailyData()
  }

  @computed
  get weekLabel(): string {
    const end = this.selectedWeekStart.plus({ days: 6 })
    const startStr = this.selectedWeekStart.toLocaleString({
      month: 'short',
      day: 'numeric',
    })
    const endStr = end.toLocaleString({
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    })
    return `${startStr} – ${endStr}`
  }

  @computed
  get selectedDayLabel(): string {
    return this.selectedDay.toLocaleString({
      weekday: 'long',
      month: 'short',
      day: 'numeric',
    })
  }

  @action
  async navigateWeek(offset: number) {
    this.selectedWeekStart = this.selectedWeekStart.plus({ weeks: offset })
    // If the selected day is outside the new week, snap to Monday
    const weekEnd = this.selectedWeekStart.plus({ days: 6 }).endOf('day')
    if (
      this.selectedDay < this.selectedWeekStart ||
      this.selectedDay > weekEnd
    ) {
      this.selectedDay = this.selectedWeekStart
    }
    await Promise.all([this.fetchWeeklyData(), this.fetchDailyData()])
  }

  @action
  async selectDay(date: DateTime) {
    this.selectedDay = date.startOf('day')
    await this.fetchDailyData()
  }

  private fieldForDay(day: DateTime): 'scheduledAt' | 'roomStartedAt' {
    const today = DateTime.now().startOf('day')
    return day < today ? 'roomStartedAt' : 'scheduledAt'
  }

  @action
  private async fetchWeeklyData() {
    this.loadingWeekly = true
    try {
      const days = Array.from({ length: 7 }, (_, i) =>
        this.selectedWeekStart.plus({ days: i })
      )

      const counts = await Promise.all(
        days.map((day) =>
          countRoomStatesInRange(this.repository, {
            start: day.startOf('day').toJSDate(),
            end: day.plus({ days: 1 }).startOf('day').toJSDate(),
            field: this.fieldForDay(day),
          })
        )
      )

      runInAction(() => {
        this.weeklyData = days.map((day, i) => ({
          date: day,
          label: day.toLocaleString({ weekday: 'short' }),
          count: counts[i],
        }))
        this.loadingWeekly = false
      })
    } catch {
      runInAction(() => {
        this.loadingWeekly = false
      })
    }
  }

  @action
  private async fetchDailyData() {
    this.loadingDaily = true
    try {
      // Sample every 30 minutes from 6:00 to 23:30
      const sampleTimes: DateTime[] = []
      for (let hour = 6; hour <= 23; hour++) {
        sampleTimes.push(this.selectedDay.set({ hour, minute: 0 }))
        sampleTimes.push(this.selectedDay.set({ hour, minute: 30 }))
      }

      const field = this.fieldForDay(this.selectedDay)
      const counts = await Promise.all(
        sampleTimes.map((t) =>
          countRoomStatesInRange(this.repository, {
            start: t.minus({ minutes: 45 }).toJSDate(),
            end: t.toJSDate(),
            field,
          })
        )
      )

      runInAction(() => {
        this.dailyData = sampleTimes.map((t, i) => ({
          time: t,
          label: t.toLocaleString({ hour: 'numeric', minute: '2-digit' }),
          count: counts[i],
        }))
        this.loadingDaily = false
      })
    } catch {
      runInAction(() => {
        this.loadingDaily = false
      })
    }
  }
}
